import ActivityAstrology from "@/assets/img/aura/activity-astrology.webp"
import ActivityConversations from "@/assets/img/aura/activity-conversations.webp"
import ActivityDancing from "@/assets/img/aura/activity-dancing.webp"
import ActivityJournaling from "@/assets/img/aura/activity-journaling.webp"
import ActivityMeditating from "@/assets/img/aura/activity-meditating.webp"
import ActivityMusic from "@/assets/img/aura/activity-music.webp"
import ActivityReading from "@/assets/img/aura/activity-reading.webp"
import ActivityRunning from "@/assets/img/aura/activity-running.webp"
import PhenomenonFullMoon from "@/assets/img/aura/phenomenon-full-moon.webp"
import PhenomenonNewMoon from "@/assets/img/aura/phenomenon-new-moon.webp"
import PhenomenonRetrogrades from "@/assets/img/aura/phenomenon-retrogrades.webp"
import PhenomenonSeasons from "@/assets/img/aura/phenomenon-seasons.webp"
import PhenomenonSolarStorms from "@/assets/img/aura/phenomenon-solar-storms.webp"
import PhenomenonThunderstorms from "@/assets/img/aura/phenomenon-thunderstorms.webp"
import RechargeAtHome from "@/assets/img/aura/recharge-at-home.webp"
import RechargeBigCity from "@/assets/img/aura/recharge-big-city.webp"
import RechargeForest from "@/assets/img/aura/recharge-forest.webp"
import RechargeMountains from "@/assets/img/aura/recharge-mountains.webp"
import RechargeOcean from "@/assets/img/aura/recharge-ocean.webp"
import RechargeVillage from "@/assets/img/aura/recharge-village.webp"
import { useLayoutEffect, useRef } from "react"
import { cn } from "@/lib/utils"
import { QuizHeader } from "../quiz-header"
import type { StepComponentProps } from "./step-contract"

const TILES: Record<string, string> = {
  at_home: RechargeAtHome,
  in_a_big_city: RechargeBigCity,
  in_the_village: RechargeVillage,
  in_a_forest: RechargeForest,
  by_the_ocean: RechargeOcean,
  in_the_mountains: RechargeMountains,
  music: ActivityMusic,
  dancing: ActivityDancing,
  conversations: ActivityConversations,
  reading: ActivityReading,
  astrology: ActivityAstrology,
  meditating: ActivityMeditating,
  running: ActivityRunning,
  journaling: ActivityJournaling,
  thunderstorms: PhenomenonThunderstorms,
  changing_seasons: PhenomenonSeasons,
  solar_storms: PhenomenonSolarStorms,
  retrogrades: PhenomenonRetrogrades,
  new_moon: PhenomenonNewMoon,
  full_moon: PhenomenonFullMoon,
}

/**
 * Two-column image-tile choice (Steps 20, 21, 23). Tapping a tile stores the
 * option and advances. Owns its header because the single-choice override
 * would render these as list rows.
 */
export function AuraImageGridStep({
  step,
  value,
  onChange,
  onAdvance,
  countedCurrent,
  countedTotal,
  onBack,
}: StepComponentProps) {
  const selected = typeof value === "string" ? value : undefined
  const rootRef = useRef<HTMLDivElement>(null)

  useLayoutEffect(() => {
    window.scrollTo(0, 0)
    rootRef.current?.scrollTo(0, 0)
  }, [step.id])

  const pick = (optionValue: string) => {
    onChange(optionValue)
    setTimeout(onAdvance, 200)
  }

  return (
    <div
      ref={rootRef}
      className="flex min-h-[100dvh] flex-1 flex-col overflow-y-auto bg-dark-base text-purple-light"
      data-slot="aura-image-grid-step"
    >
      <QuizHeader current={countedCurrent} total={countedTotal} showBack onBack={onBack} />

      <div className="flex flex-1 flex-col px-[21px] pt-8 pb-6">
        <h1 className="text-center font-display text-2xl font-medium text-purple-light">
          {step.title}
        </h1>

        <div className="mt-8 grid grid-cols-2 gap-3">
          {(step.options ?? []).map((option) => {
            const src = TILES[option.value]
            const isSelected = selected === option.value
            return (
              <button
                key={option.value}
                type="button"
                onClick={() => pick(option.value)}
                className={cn(
                  "relative h-[230px] overflow-hidden rounded-2xl",
                  isSelected && "ring-2 ring-main-purple",
                )}
              >
                {src && (
                  <img src={src} alt="" className="absolute inset-0 size-full object-cover object-bottom" />
                )}
                <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-dark-base to-transparent" />
                <span className="absolute inset-x-0 bottom-0 flex h-[42px] items-center justify-center rounded-2xl bg-purple-light/10 text-base font-semibold text-purple-light backdrop-blur-[20px]">
                  {option.label}
                </span>
              </button>
            )
          })}
        </div>
      </div>
    </div>
  )
}
